/* eslint-disable react/prop-types */
import "prismjs/themes/prism.css";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { CookiesProvider } from "react-cookie";
import { PageContextWrapper } from "./src/layouts/PageContextWrapper";
import { trackPageTag } from "./src/helpers/tagTracking";
import { captureUtmParams } from "./src/helpers/utmTracking";

const isDevelopment = process.env.NODE_ENV === "development";
const isEnBuild = process.env.GATSBY_TARGET_LANGUAGE !== "ru";
let reactGaPromise;

const loadGoogleAnalytics = () => {
  if (isDevelopment || !isEnBuild) {
    return Promise.resolve(null);
  }

  if (!reactGaPromise) {
    reactGaPromise = import("react-ga/dist/esm/index").then(
      ({ default: ReactGA }) => {
        ReactGA.initialize([
          {
            trackingId: "UA-44791317-1",
            gaOptions: {
              "Cache-Control": "max-age = 86400",
              cookieExpires: 86400,
            },
          },
        ]);

        return ReactGA;
      },
    );
  }

  return reactGaPromise;
};

const WrappedPageElement = ({ element, props }) => {
  useEffect(() => {
    if (isDevelopment || !isEnBuild) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      loadGoogleAnalytics().then((ReactGA) => {
        if (!ReactGA) {
          return;
        }

        ReactGA.ga("set", {
          page: window.location.pathname,
          title: window.document.title,
        });
        ReactGA.ga("send", {
          hitType: "pageview",
          page: window.location.pathname,
          title: window.document.title,
        });
      });
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return React.createElement(PageContextWrapper, props, element);
};

export const onClientEntry = () => {
  if (isDevelopment || !isEnBuild) {
    return;
  }

  const initializeAnalytics = () => {
    loadGoogleAnalytics();
  };

  if (document.readyState === "complete") {
    initializeAnalytics();
    return;
  }

  window.addEventListener("load", initializeAnalytics, { once: true });
};

export const onInitialClientRender = () => {
  trackPageTag(window.location.pathname);
  captureUtmParams(window.location.search);
};

export const onRouteUpdate = ({ location }) => {
  trackPageTag(location.pathname);
  captureUtmParams(location.search);
};

export const wrapRootElement = ({ element }) => (
  <CookiesProvider>
    {!isDevelopment && isEnBuild && (
      <Helmet>
        <link
          rel="preconnect"
          href="https://stats.g.doubleclick.net"
          crossOrigin
        />
        <link
          rel="preconnect"
          href="https://www.google-analytics.com/"
          crossOrigin
        />
        <link
          rel="preconnect"
          href="https://www.googletagmanager.com/"
          crossOrigin
        />
      </Helmet>
    )}
    {!isDevelopment && !isEnBuild && (
      <Helmet>
        <link
          rel="preconnect"
          href="https://mc.yandex.ru/webvisor"
          crossOrigin
        />
      </Helmet>
    )}
    {element}
  </CookiesProvider>
);

export const wrapPageElement = ({ element, props }) => (
  <WrappedPageElement element={element} props={props} />
);
